// src/pages/AnnuaireBulletinConjoncturel.jsx
import React, { useState, useEffect, useMemo } from 'react';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  styled,
  createTheme,
  ThemeProvider,
  Box,
  CardMedia,
  Button,
} from '@mui/material';
import moment from 'moment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import { getPublications } from '../services/publicationService';

function AnnuaireBulletinConjoncturel() {
  const [visibleCount, setVisibleCount] = useState(2);
  const [selectedYear, setSelectedYear] = useState('Tous');
  const [bulletinsData, setBulletinsData] = useState([]);
  const [filteredBulletins, setFilteredBulletins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Chargement des données depuis l'API
  useEffect(() => {
    setLoading(true);
    getPublications({ type: 'bcsf', perPage: 1000 })
      .then((res) => {
        if (res.error) {
          setError(res.error);
        } else {
          // Mapper les données de l'API vers la structure attendue
          const mapped = res.data.map((item) => ({
            title: item.title,
            principalLink: item.document_url,
            image: item.image_url,
            description: item.description,
            date: new Date(item.date), // utilisé pour le filtre
            publicationDate: item.publication_date ? new Date(item.publication_date) : null,
          }));
          // Trier par date décroissante (plus récent en premier)
          const sorted = mapped.sort((a, b) => b.date - a.date);
          setBulletinsData(sorted);
          setFilteredBulletins(sorted);
        }
      })
      .catch((err) => {
        setError('Erreur lors du chargement des données.');
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleShowMore = () => {
    setVisibleCount((prevCount) => prevCount + 2);
  };

  const handleYearChange = (event) => {
    const year = event.target.value;
    setSelectedYear(year);
    let filtered;
    if (year === 'Tous') {
      filtered = [...bulletinsData];
    } else {
      filtered = bulletinsData.filter((bulletin) => {
        const bulletinYear = bulletin.date.getFullYear();
        return bulletinYear === parseInt(year);
      });
    }
    // Trier par date décroissante
    filtered.sort((a, b) => b.date - a.date);
    setFilteredBulletins(filtered);
    // visibleCount reste inchangé (comportement statique)
  };

  const StyledSelect = styled(Select)(({ theme }) => ({
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.light,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.dark,
    },
  }));

  const theme = createTheme({
    palette: {
      primary: { main: '#388E3C' },
      background: { default: '#f5f5f5' },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    },
  });

  // Années disponibles à partir des données chargées
  const availableYears = useMemo(() => {
    const years = bulletinsData.map((bulletin) => bulletin.date.getFullYear());
    return [...new Set(years)].sort((a, b) => b - a);
  }, [bulletinsData]);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
          <NavigationBar />
          <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main }}>
              Bulletin Conjoncturel des Statistiques aux Frontières
            </Typography>
            <Divider style={{ margin: '16px 0' }} />
            <Typography variant="body1">Chargement des données...</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
          <NavigationBar />
          <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
            <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main }}>
              Bulletin Conjoncturel des Statistiques aux Frontières
            </Typography>
            <Divider style={{ margin: '16px 0' }} />
            <Typography variant="body1" color="error">{error}</Typography>
          </Container>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div style={{ backgroundColor: theme.palette.background.default, paddingBottom: '32px' }}>
        <NavigationBar />

        <Container maxWidth="lg" style={{ paddingTop: '54px' }}>
          <Typography variant="h4" align="justify" gutterBottom style={{ color: theme.palette.primary.main }}>
            Bulletin Conjoncturel des Statistiques aux Frontières
          </Typography>
          <Divider style={{ margin: '16px 0' }} />
          <Typography variant="body1" align="justify" paragraph style={{ marginBottom: '24px' }}>
            Le Bulletin conjoncturel des statistiques aux frontières est un document essentiel qui présente une synthèse des analyses statistiques relatives aux mouvements de population aux frontières, en particulier aux flux des touristes internationaux venus et sortis du Burundi durant la période d’expérimentation de la nouvelle carte d’Entrée-Sortie. Depuis octobre 2010, avec le soutien du PNUD et de l’ISTEEBU, l’enregistrement des Entrées et Sorties aux postes frontières a permis une meilleure compréhension des flux migratoires. Une phase d’expérimentation de trois mois a été prévue pour tester la première version de cette carte.
          </Typography>

          <FormControl fullWidth variant="outlined" style={{ marginLeft: '0px', width: '300px' }}>
            <InputLabel id="year-select-label">Sélectionner par l'année de publication</InputLabel>
            <StyledSelect
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              onChange={handleYearChange}
              label="Sélectionner par l'année de publication"
            >
              <MenuItem value="Tous">Toutes les statistiques</MenuItem>
              {availableYears.map((year) => (
                <MenuItem key={year} value={year.toString()}>
                  {year}
                </MenuItem>
              ))}
            </StyledSelect>
          </FormControl>

          {/* Affichage des bulletins conjoncturels */}
          <Grid container spacing={1} style={{ marginTop: '10px' }}>
            {filteredBulletins.slice(0, visibleCount).map((bulletin, index) => (
              <Grid item xs={12} sm={12} key={index}>
                <Card elevation={3} style={{ borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <Box sx={{ display: 'flex' }}>
                    <a
                      target="_blank"
                      href={bulletin.principalLink}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                      rel="noopener noreferrer"
                    >
                      <CardMedia
                        component="img"
                        sx={{ width: 71, height: 120, m: 2 }}
                        image={bulletin.image}
                        alt={bulletin.title}
                      />
                    </a>
                    <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2, pt: 2 }}>
                      <Typography variant="h6" component="div" style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                        <a
                          href={bulletin.principalLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none', color: theme.palette.primary.main }}
                        >
                          <DownloadIcon /> {bulletin.title}
                        </a>
                      </Typography>
                      <Typography variant="body2" component="div" style={{ color: '#555', marginBottom: '10px' }}>
                        {bulletin.description}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Publié en {moment(bulletin.publicationDate).format('MMMM YYYY')}
                      </Typography>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {visibleCount < filteredBulletins.length && (
            <Button
              onClick={handleShowMore}
              style={{
                marginTop: '20px',
                marginBottom: '20px',
                backgroundColor: theme.palette.primary.main,
                color: 'white',
              }}
            >
              Plus de Statistiques
            </Button>
          )}
          <br />
          <Box display="flex" justifyContent="flex-start" alignItems="center">
            <Button
              onClick={() => navigate('/annuaire')}
              style={{
                marginTop: '5px',
                backgroundColor: theme.palette.primary.main,
                color: 'white',
              }}
            >
              <ArrowBackIcon style={{ marginRight: '8px' }} />
              Retour à la page principale
            </Button>
          </Box>
        </Container>
        <br />
        <br />
        <Footer />
      </div>
    </ThemeProvider>
  );
}

export default AnnuaireBulletinConjoncturel;